import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Navigate, useSearch } from '@tanstack/react-router';
import { Accordion, Button, Typography } from '@volvo/vcdk-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import StylizedUnitsIllustration from '@/assets/multiple_stylized_units_v2.svg';
import AddNewUnitModal from '@/components/modals/unit-registration/AddNewUnitModal';
import { analyticsEvents } from '@/contexts/analytics/events';
import { allUnitsQueryOptions } from '@/service/vpc/queryOptions';

const unitsSearchSchema = z.object({
  register: z.coerce.boolean().optional(),
  sn: z.string().optional(),
  guid: z.string().optional(),
  origin: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/welcome/')({
  component: WelcomePage,
  validateSearch: unitsSearchSchema,
});

function WelcomePage() {
  const { t } = useTranslation();
  const { data: allUnits, isFetched, isLoading } = useQuery(allUnitsQueryOptions());
  const { register, sn, origin, guid } = useSearch({
    from: '/_authenticated/welcome/',
  });

  if (isLoading || !isFetched) {
    return <></>;
  }

  if (allUnits && allUnits.length > 0) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <div className="mx-auto flex max-w-[364px] flex-col text-center">
        <img src={StylizedUnitsIllustration} alt="boats and industrial machines" className="mb-6 w-full" />
        <Typography variant="heading3" className="mb-4">
          {t(($) => $.pages.welcome.title)}
        </Typography>
        <Typography variant="body">{t(($) => $.pages.welcome.body)}</Typography>
        <div className="mt-6 flex w-full justify-center gap-4">
          <Button variant="secondary" href="/support">
            {t(($) => $.pages.welcome.get_help)}
          </Button>
          <AddNewUnitModal
            initialSerialNumber={sn}
            autoOpen={register === true && !!sn}
            autoSearch={register === true && !!sn}
            origin={origin}
            qrCode={guid}
          />
        </div>
        <div className="mt-9 text-left">
          <Typography variant="subtitle2" className="mb-4">
            {t(($) => $.pages.welcome.get_started)}
          </Typography>
          <Accordion
            style={
              {
                '--vcdk-accordion-padding-inline': '0',
              } as React.CSSProperties
            }
            size="small"
            className="[&::part(details)]:bg-base w-full"
            onClick={() => {
              analyticsEvents.Interaction.componentInteraction({
                parent: 'WelcomePage',
                type: 'Accordion',
                title: 'Who is it for?',
                placement: 'WelcomePage',
              });
            }}
          >
            <span slot="title">{t(($) => $.pages.welcome.accordion.who_is_it_for.title)}</span>
            <Typography variant="caption1">{t(($) => $.pages.welcome.accordion.who_is_it_for.body)}</Typography>
          </Accordion>
          <Accordion
            style={
              {
                '--vcdk-accordion-padding-inline': '0',
              } as React.CSSProperties
            }
            size="small"
            className="[&::part(details)]:bg-base w-full"
            onClick={() => {
              analyticsEvents.Interaction.componentInteraction({
                parent: 'WelcomePage',
                type: 'Accordion',
                title: 'How to register?',
                placement: 'WelcomePage',
              });
            }}
          >
            <span slot="title">{t(($) => $.pages.welcome.accordion.how_to_register.title)}</span>
            <Typography variant="caption1">{t(($) => $.pages.welcome.accordion.how_to_register.body)}</Typography>
          </Accordion>
          <Accordion
            style={
              {
                '--vcdk-accordion-padding-inline': '0',
              } as React.CSSProperties
            }
            size="small"
            className="[&::part(details)]:bg-base w-full"
            onClick={() => {
              analyticsEvents.Interaction.componentInteraction({
                parent: 'WelcomePage',
                type: 'Accordion',
                title: 'Can I use without registering?',
                placement: 'WelcomePage',
              });
            }}
          >
            <span slot="title">{t(($) => $.pages.welcome.accordion.can_i_use_without_registering.title)}</span>
            <Typography variant="caption1">
              {t(($) => $.pages.welcome.accordion.can_i_use_without_registering.body)}
            </Typography>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
